import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Enhanced UTM helpers: comprehensive tracking and attribution
export function captureFirstTouchUtm() {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    if ([...params.keys()].length === 0) return;
    
    const utm: Record<string, string> = {};
    const trackingParams = [
      'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
      'gclid', 'fbclid', 'msclkid', 'ttclid', 'li_fat_id',
      'source', 'medium', 'campaign', 'term', 'content'
    ];
    
    params.forEach((v, k) => {
      if (trackingParams.includes(k)) {
        utm[k] = v;
      }
    });
    
    if (Object.keys(utm).length === 0) return;
    
    // Add additional tracking data
    const enhancedUtm = {
      ...utm,
      ts: Date.now(),
      referrer: document.referrer || '',
      landing_page: window.location.href,
      user_agent: navigator.userAgent,
      screen_resolution: `${screen.width}x${screen.height}`,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
    
    if (!localStorage.getItem('fh_first_touch_utm')) {
      localStorage.setItem('fh_first_touch_utm', JSON.stringify(enhancedUtm));
    }
    
    // Also capture last touch for attribution
    localStorage.setItem('fh_last_touch_utm', JSON.stringify(enhancedUtm));
  } catch {}
}

export function getPersistedUtm(): Record<string, string> {
  if (typeof window === 'undefined') return {} as any;
  try {
    // First check current URL for UTM parameters
    const currentParams = new URLSearchParams(window.location.search);
    const currentUtm: Record<string, string> = {};
    const trackingParams = [
      'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
      'gclid', 'fbclid', 'msclkid', 'ttclid', 'li_fat_id',
      'source', 'medium', 'campaign', 'term', 'content'
    ];
    
    currentParams.forEach((v, k) => {
      if (trackingParams.includes(k)) {
        currentUtm[k] = v;
      }
    });
    
    // If current URL has UTM parameters, use them (last touch)
    if (Object.keys(currentUtm).length > 0) {
      return {
        ...currentUtm,
        referrer: document.referrer || '',
        landing_page: window.location.href
      };
    }
    
    // Otherwise, use first touch data
    const raw = localStorage.getItem('fh_first_touch_utm');
    if (!raw) return {} as any;
    const parsed = JSON.parse(raw);
    delete parsed.ts;
    delete parsed.user_agent;
    delete parsed.screen_resolution;
    delete parsed.language;
    delete parsed.timezone;
    return parsed || {};
  } catch { return {} as any; }
}

export function getFirstTouchUtm(): Record<string, string> {
  if (typeof window === 'undefined') return {} as any;
  try {
    const raw = localStorage.getItem('fh_first_touch_utm');
    if (!raw) return {} as any;
    const parsed = JSON.parse(raw);
    delete parsed.ts;
    delete parsed.user_agent;
    delete parsed.screen_resolution;
    delete parsed.language;
    delete parsed.timezone;
    return parsed || {};
  } catch { return {} as any; }
}

export function getLastTouchUtm(): Record<string, string> {
  if (typeof window === 'undefined') return {} as any;
  try {
    const raw = localStorage.getItem('fh_last_touch_utm');
    if (!raw) return {} as any;
    const parsed = JSON.parse(raw);
    delete parsed.ts;
    delete parsed.user_agent;
    delete parsed.screen_resolution;
    delete parsed.language;
    delete parsed.timezone;
    return parsed || {};
  } catch { return {} as any; }
}

export function clearUtmData() {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem('fh_first_touch_utm');
    localStorage.removeItem('fh_last_touch_utm');
  } catch {}
}
